import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, TrendingUp } from 'lucide-react';
import { useWordCount } from '../../shared/hooks/useWordCount';

interface UpgradePromptProps {
  isVisible: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  isVisible,
  onClose,
  onUpgrade,
}) => {
  const { wordCount } = useWordCount();
  const [displayTime, setDisplayTime] = useState('');
  
  useEffect(() => {
    // Calculate time saved (approximation: 30 WPM typing vs instant voice)
    const minutesSaved = Math.round(wordCount.enhancedWords / 30);
    
    if (minutesSaved < 60) {
      setDisplayTime(`${minutesSaved} minutes`);
    } else {
      const hours = (minutesSaved / 60).toFixed(1);
      setDisplayTime(`${hours} hours`);
    }
  }, [wordCount.enhancedWords]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Golden gradient background effect */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-transparent to-orange-50 opacity-50" />
            </div>

            {/* Content */}
            <div className="relative">
              {/* Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg"
              >
                <Sparkles className="w-8 h-8 text-white" />
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-light text-gray-900 text-center mb-3"
              >
                Amazing Progress!
              </motion.h2>

              {/* Stats */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-4 mb-6"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Words Enhanced</span>
                  <span className="text-xl font-semibold text-gray-900">
                    {wordCount.enhancedWords.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Time Saved</span>
                  <span className="text-xl font-semibold text-gray-900 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1 text-green-500" />
                    {displayTime}
                  </span>
                </div>
              </motion.div>

              {/* Message */}
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-gray-600 text-center mb-6"
              >
                You've reached your monthly limit of {wordCount.monthlyLimit.toLocaleString()} words.
                Upgrade to unlimited for seamless productivity.
              </motion.p>

              {/* Buttons */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col space-y-3"
              >
                <button
                  onClick={onUpgrade}
                  className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-all transform hover:scale-105 shadow-lg"
                >
                  Upgrade to Unlimited
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Maybe Later
                </button>
              </motion.div>

              {/* Pro tip */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-xs text-gray-500 text-center mt-4"
              >
                Pro tip: Unlimited users save an average of 5+ hours per month
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};